import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Layout from '../layout/Layout'
import VehicleDetailsCard from '../molecules/VehicleDetailsCard'
import ClientDocumentsPanel from '../molecules/ClientDocumentsPanel'
import { formatClientDetailsName } from 'src/helpers/formatClientDetailsName'
import { VehicleDocumentType, documentEditPath, vehicleDocumentCreatePath } from 'src/helpers/addVehicleDocument'
import { FETCH_CLIENT, Client } from 'src/redux/clientReducer'
import {
  FETCH_DOCUMENTS,
  FETCH_PDF_URL,
  RESET_PDF_URL,
  SET_CURRENT_PAGE,
  DELETE_DOCUMENT,
  Document
} from 'src/redux/documentsReducer'
import { RootReducer } from 'src/redux/rootReducer'

const displayValue = (value: string | null | undefined): string => {
  if (value === null || typeof value === 'undefined' || value.trim().length === 0) {
    return '-'
  }
  return value
}

const ClientDetails = (): JSX.Element => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const pdfUrl = useSelector((state: RootReducer) => state.documentsReducer.pdf_url)
  const pdfIntent = useSelector((state: RootReducer) => state.documentsReducer.pdf_intent)
  const documents = useSelector((state: RootReducer) => state.documentsReducer.documents)
  const currentPage = useSelector((state: RootReducer) => state.documentsReducer.currentPage)
  const totalPages = useSelector((state: RootReducer) => state.documentsReducer.totalPages)

  const client = useSelector((state: RootReducer) => {
    if (typeof id !== 'string') {
      return null
    }
    return state.clientReducer.client[id] ?? null
  })

  useEffect(() => {
    dispatch(SET_CURRENT_PAGE(1))
  }, [dispatch])

  useEffect(() => {
    if (typeof id === 'string') {
      dispatch(FETCH_CLIENT(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (typeof id === 'string') {
      dispatch(FETCH_DOCUMENTS({ clientId: id }))
    }
  }, [dispatch, id, currentPage])

  useEffect(() => {
    return () => {
      dispatch(RESET_PDF_URL())
    }
  }, [dispatch])

  useEffect(() => {
    if (pdfUrl === '') {
      return
    }
    if (pdfIntent === 'download') {
      const link = window.document.createElement('a')
      link.href = pdfUrl
      link.download = 'document.pdf'
      link.rel = 'noopener noreferrer'
      link.target = '_blank'
      link.click()
    } else {
      window.open(pdfUrl, '_blank')
    }
    dispatch(RESET_PDF_URL())
  }, [dispatch, pdfIntent, pdfUrl])

  const title = client === null ? t('clients.details') : formatClientDetailsName(client)

  const countryLabel = (value: string): string => {
    if (value === 'poland') {
      return t('clients.countries.poland')
    }
    if (value === 'germany') {
      return t('clients.countries.germany')
    }
    return displayValue(value)
  }

  const fullName = (current: Client): string => {
    return displayValue(`${current.first_name} ${current.last_name}`.trim())
  }

  const handleSelectDocumentType = (documentType: VehicleDocumentType): void => {
    if (typeof id === 'string') {
      navigate(vehicleDocumentCreatePath({ documentType, clientId: id }))
    }
  }

  const handleOpenDocument = (document: Document): void => {
    dispatch(FETCH_PDF_URL({ id: document.id, document_type: document.document_type, intent: 'open' }))
  }

  const handleDownloadDocument = (document: Document): void => {
    dispatch(FETCH_PDF_URL({ id: document.id, document_type: document.document_type, intent: 'download' }))
  }

  const handleEditDocument = (document: Document): void => {
    const path = documentEditPath({
      documentType: document.document_type,
      documentId: document.id,
      clientId: document.client_id ?? id
    })
    if (path !== null) {
      navigate(path)
    }
  }

  const handleDeleteDocument = (document: Document): void => {
    dispatch(
      DELETE_DOCUMENT({
        id: document.id,
        document_type: document.document_type,
        ...(typeof id === 'string' ? { clientId: id } : {})
      })
    )
  }

  const handleOpenVehicle = (document: Document): void => {
    const motorbikeId = document.motorbike_id
    if (motorbikeId === null || typeof motorbikeId === 'undefined' || String(motorbikeId).length === 0) {
      return
    }
    navigate(`/selling/vehicles/${motorbikeId}`)
  }

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        <VehicleDetailsCard
          title={title}
          status={null}
          backLabel={t('clients.title')}
          specColumns={5}
          largeTitle
          editVariant="more"
          specs={[
            { label: t('clients.tax_number'), value: displayValue(client?.tax_number) },
            { label: t('clients.vat_number'), value: displayValue(client?.vat_number) },
            { label: t('clients.full_name'), value: client === null ? '-' : fullName(client) },
            { label: t('clients.street'), value: displayValue(client?.street) },
            { label: t('clients.house_number'), value: displayValue(client?.house_number) },
            { label: t('clients.country'), value: client === null ? '-' : countryLabel(client.country) },
            { label: t('clients.zip_code'), value: displayValue(client?.zip_code) },
            { label: t('clients.city'), value: displayValue(client?.place) },
            { label: t('clients.phone'), value: displayValue(client?.phone_number) },
            { label: t('clients.email'), value: displayValue(client?.email_address ?? client?.email) }
          ]}
          onBack={() => {
            navigate('/selling/clients')
          }}
          onEdit={() => {
            if (typeof id === 'string') {
              navigate(`/selling/clients/${id}/edit`)
            }
          }}
        />
        <div className="self-stretch flex-1 relative inline-flex justify-start items-start gap-4">
          <ClientDocumentsPanel
            documents={documents}
            currentPage={currentPage}
            totalPages={totalPages}
            onSelectDocumentType={handleSelectDocumentType}
            onOpenDocument={handleOpenDocument}
            onDownloadDocument={handleDownloadDocument}
            onEditDocument={handleEditDocument}
            onDeleteDocument={handleDeleteDocument}
            onOpenVehicle={handleOpenVehicle}
            onPageChange={(page) => {
              dispatch(SET_CURRENT_PAGE(page))
            }}
          />
        </div>
      </div>
    </Layout>
  )
}

export default ClientDetails
